import type { types } from '@bedc/types';

import type { Projection } from './types';

type Ring = types.ui.geometry.Path;

/**
 * The y-values where the vertical line `x = at` crosses a closed ring.
 *
 * Even-odd semantics with the same strict-straddle convention as
 * `cutoutUtils.isPointInRing`, so a line grazing a vertex counts once.
 */
const crossings = (ring: Ring, at: number): number[] => {
	const ys: number[] = [];

	for (let i = 0; i < ring.length - 1; i++) {
		const p = ring[i];
		const q = ring[i + 1];
		if (!p || !q) continue;

		if (p.x > at !== q.x > at) {
			ys.push(p.y + ((at - p.x) / (q.x - p.x)) * (q.y - p.y));
		}
	}

	return ys.sort((a, b) => a - b);
};

/** Pair sorted crossings into inside-intervals. */
const intervals = (ys: number[]): [number, number][] => {
	const out: [number, number][] = [];
	for (let i = 0; i + 1 < ys.length; i += 2) out.push([ys[i]!, ys[i + 1]!]);
	return out;
};

/** Remove the hole intervals from the inside intervals. */
const subtract = (inside: [number, number][], holes: [number, number][]): [number, number][] => {
	let out = inside;

	for (const [h0, h1] of holes) {
		const next: [number, number][] = [];
		for (const [a, b] of out) {
			if (h1 <= a || h0 >= b) {
				next.push([a, b]);
				continue;
			}
			if (a < h0) next.push([a, h0]);
			if (h1 < b) next.push([h1, b]);
		}
		out = next;
	}

	return out;
};

/**
 * Rotate rings by an angle about the origin: how both projections bring the
 * line direction onto the vertical scan axis.
 *
 * @param rad The rotation, in radians.
 * @returns The rotation of one ring.
 */
const rotateRing =
	(rad: number) =>
	(ring: Ring): Ring =>
		ring.map((point) => ({
			x: point.x * Math.cos(rad) - point.y * Math.sin(rad),
			y: point.x * Math.sin(rad) + point.y * Math.cos(rad),
		}));

/**
 * Project parallel framing lines into an area layer at a spacing and angle.
 *
 * Takes the PERSISTED LAYER SHAPE: every outer ring in `paths`, clipped by
 * every opening in `cutouts` (clip-decides — an opening affects each ring it
 * overlaps and nothing outside them). Per-ring phase is internal: each ring
 * lays out from its own left extent at half-spacing offset, so drawing a new
 * ring never re-lays an existing one. Origin ("Start Layout Here") is a
 * deferred recipe parameter.
 *
 * Rings are rotated into LINE SPACE (lines vertical), scanned, and segments
 * reported in that frame — lengths and counts are frame-invariant; rendering
 * rotates back. Angle runs anticlockwise from the HORIZONTAL axis — 0° is
 * horizontal lines, 90° vertical, per the product convention.
 *
 * @param paths The layer's outer rings, each explicitly closed.
 * @param cutouts Openings to clip against, each explicitly closed.
 * @param spacing On-centre spacing in drawing units.
 * @param angleDegree Line direction, degrees anticlockwise from horizontal.
 * @param minLength Drawing-unit noise floor for degenerate grazes; the REAL
 *   sliver rule is the resolution in `segmentLengths`.
 */
export const projectLineSegmentsFromArea = (
	paths: Ring[],
	cutouts: Ring[],
	spacing: number,
	angleDegree = 90,
	minLength = 0
): Projection => {
	// ! A non-positive or non-finite spacing cannot advance the scan loop — a
	// cleared or half-typed spacing input must blank the projection, never
	// hang the evaluator.
	if (!Number.isFinite(spacing) || spacing <= 0) {
		return { segments: [], linesPlaced: 0, linesSplit: 0 };
	}

	// Rotate by (angle − 90°) so the line direction lands on the vertical scan
	// axis; at 90° — vertical, the default — this is the identity.
	const rotate = rotateRing(((angleDegree - 90) * Math.PI) / 180);

	const lineSpaceCutouts = cutouts.map(rotate);
	const segments: Projection['segments'] = [];
	let linesPlaced = 0;
	let linesSplit = 0;

	for (const path of paths) {
		const ring = rotate(path);
		const xs = ring.map((point) => point.x);
		const maxX = Math.max(...xs);

		for (let x = Math.min(...xs) + spacing / 2; x < maxX; x += spacing) {
			const inside = intervals(crossings(ring, x));
			if (inside.length === 0) continue;

			linesPlaced += 1;

			const holes = lineSpaceCutouts.flatMap((cutout) => intervals(crossings(cutout, x)));
			const clipped = subtract(inside, holes).filter(([a, b]) => b - a > minLength);

			if (clipped.length > inside.length) linesSplit += 1;

			for (const [y0, y1] of clipped) segments.push({ x, y0, y1 });
		}
	}

	return { segments, linesPlaced, linesSplit };
};

/** One projected line in drawing coordinates, ready to render. */
export type DrawingLine = { x0: number; y0: number; x1: number; y1: number };

/**
 * Map a projection's line-space segments back into drawing coordinates.
 *
 * The exact inverse of the rotation `projectLineSegmentsFromArea` applies, so
 * each segment's endpoints land where the framing line crosses the drawn area.
 * At the default 90° the rotation is the identity: vertical lines at drawing
 * `x` spanning `y0..y1`.
 *
 * @param projection The projection whose segments to map.
 * @param angleDegree The angle the projection was computed at.
 * @returns One drawing-space line per clipped segment.
 */
export const projectionToDrawingLines = (
	projection: Projection,
	angleDegree = 90
): DrawingLine[] => {
	const rad = ((angleDegree - 90) * Math.PI) / 180;
	const unrotate = (x: number, y: number) => ({
		x: x * Math.cos(rad) + y * Math.sin(rad),
		y: -x * Math.sin(rad) + y * Math.cos(rad),
	});

	return projection.segments.map((segment) => {
		const p0 = unrotate(segment.x, segment.y0);
		const p1 = unrotate(segment.x, segment.y1);

		return { x0: p0.x, y0: p0.y, x1: p1.x, y1: p1.y };
	});
};

/** How generated lines are laid out across an area. */
export type LineLayout = {
	/** Centres in drawing units: exact (`fixed`) or the largest allowed (`max`). */
	spacing: number;
	/** Line direction, degrees anticlockwise from horizontal. */
	angleDegree: number;
	/** `fixed` steps at exactly `spacing`; `max` spreads as few lines as keep within it evenly. */
	mode: 'fixed' | 'max';
	/** Where a fixed run is anchored: the run's first edge, its centre, or its far edge. */
	start: 'begin' | 'center' | 'end';
	/** The first line's distance in from the anchor, in drawing units. */
	firstOffset: number;
	/** One grid across every ring (`shared`), or a grid per ring (`each`). */
	scope: 'shared' | 'each';
};

/** The most lines one run may place, so a tiny spacing cannot stall the page. */
const maxLinesPerRun = 5000;

/**
 * Line positions across one run, in line space.
 *
 * @param min The run's first edge.
 * @param max The run's far edge.
 * @param layout The layout.
 * @returns The positions within the run, ascending.
 */
const runPositions = (min: number, max: number, layout: LineLayout): number[] => {
	const { spacing, firstOffset } = layout;
	const length = max - min;
	const slack = 1e-9 * Math.max(1, length);
	if (length / spacing > maxLinesPerRun) return [];

	if (layout.mode === 'max') {
		const usable = length - 2 * firstOffset;
		if (usable <= slack) return [min + length / 2];
		const bays = Math.max(1, Math.ceil(usable / spacing - 1e-9));
		return Array.from(
			{ length: bays + 1 },
			(_, index) => min + firstOffset + (index * usable) / bays
		);
	}

	const anchor =
		layout.start === 'begin'
			? min + firstOffset
			: layout.start === 'end'
				? max - firstOffset
				: min + length / 2 + firstOffset;
	// An edge start runs away from its edge only, so its first line is the one
	// at the offset; a centre start runs both ways.
	const before = layout.start === 'begin' ? 0 : Math.floor((anchor - min + slack) / spacing);
	const after = layout.start === 'end' ? 0 : Math.floor((max - anchor + slack) / spacing);

	// An offset past the far edge leaves positions outside the run; drop them
	// rather than let the edge clamp stack them on the boundary.
	return Array.from(
		{ length: before + after + 1 },
		(_, index) => anchor + (index - before) * spacing
	).filter((position) => position >= min - slack && position <= max + slack);
};

/**
 * Lay lines across an area layer to a chosen layout — the "Generate lines"
 * action. Unlike the recipe projection, which always starts half a spacing in
 * from each ring's left, the run's anchor, first offset, spacing rule and
 * whether rings share one grid are all chosen.
 *
 * A line on the very edge is nudged just inside so it is kept, not grazed away.
 *
 * @param paths The layer's outer rings, each explicitly closed.
 * @param cutouts Openings to clip against, each explicitly closed.
 * @param layout The layout, in drawing units.
 * @returns The segments in line space; `projectionToDrawingLines` maps them back.
 */
export const layoutLineSegmentsInArea = (
	paths: Ring[],
	cutouts: Ring[],
	layout: LineLayout
): Projection => {
	if (!Number.isFinite(layout.spacing) || layout.spacing <= 0 || !paths.length) {
		return { segments: [], linesPlaced: 0, linesSplit: 0 };
	}

	const rotate = rotateRing(((layout.angleDegree - 90) * Math.PI) / 180);

	const rings = paths.map(rotate);
	const lineSpaceCutouts = cutouts.map(rotate);
	const runs = layout.scope === 'each' ? rings.map((ring) => [ring]) : [rings];
	const segments: Projection['segments'] = [];
	let linesPlaced = 0;
	let linesSplit = 0;

	for (const run of runs) {
		const xs = run.flatMap((ring) => ring.map((point) => point.x));
		const min = Math.min(...xs);
		const max = Math.max(...xs);
		const nudge = 1e-6 * Math.max(1, max - min);

		for (const position of runPositions(min, max, layout)) {
			const x = Math.min(max - nudge, Math.max(min + nudge, position));
			const inside = run
				.flatMap((ring) => intervals(crossings(ring, x)))
				.sort((left, right) => left[0] - right[0]);
			if (inside.length === 0) continue;

			linesPlaced += 1;

			const holes = lineSpaceCutouts.flatMap((cutout) => intervals(crossings(cutout, x)));
			const clipped = subtract(inside, holes).filter(([a, b]) => b - a > nudge);

			if (clipped.length > inside.length) linesSplit += 1;

			for (const [y0, y1] of clipped) segments.push({ x, y0, y1 });
		}
	}

	return { segments, linesPlaced, linesSplit };
};
